import React, { useEffect, useRef } from "react";
import "./Cloud.css";

// Layer artwork of the original scene (creativeocean, "ScrollTrigger: SVG Text Mask").
const ASSET_BASE = "https://assets.codepen.io/721952/";

// How far the visitor has to scroll while the scene stays pinned, relative to
// the viewport height. The parallax runs over the first part of that distance
// and the final, whited-out "FURTHER" frame is held for the rest (END_HOLD),
// so the hand-over to the next section feels calm instead of abrupt.
const PIN_DISTANCE = "+=200%";
const END_HOLD = 0.03;

const Cloud = () => {
	const sceneRef = useRef(null);

	useEffect(() => {
		const { gsap, ScrollTrigger, ScrollToPlugin } = window;
		const scene = sceneRef.current;
		if (!gsap || !ScrollTrigger || !scene) return undefined;

		gsap.registerPlugin(ScrollTrigger);
		if (ScrollToPlugin) gsap.registerPlugin(ScrollToPlugin);

		const q = gsap.utils.selector(scene);

		const tl = gsap.timeline({
			defaults: { ease: "none", duration: 1 },
			scrollTrigger: {
				trigger: scene,
				start: "top top",
				end: PIN_DISTANCE,
				pin: true,
				scrub: 1,
				anticipatePin: 1,
				invalidateOnRefresh: true,
			},
		});

		tl
			// Far layers travel little, near layers travel a lot: that is the depth.
			// The proportions follow the original scene; every layer just travels more.
			.fromTo(q(".sky"), { y: 0 }, { y: -200 }, 0)
			.fromTo(q(".mountBg"), { y: -10 }, { y: -160 }, 0)
			.fromTo(q(".mountMg"), { y: -30 }, { y: -400 }, 0)
			.fromTo(q(".cloud2"), { y: -150 }, { y: -600 }, 0)
			.fromTo(q(".mountFg"), { y: -50 }, { y: -650 }, 0)
			.fromTo(q(".cloud3"), { y: -50 }, { y: -750 }, 0)
			// Both headlines share one drift so "FURTHER" is revealed exactly where
			// "EXPLORE" was: the letters swap cleanly as the fog passes over them.
			.fromTo(q(".headline, .text-further"), { y: 0 }, { y: 150 }, 0)
			// The fog bank (visible cloud + its copy inside the mask) rises fastest of
			// all and swallows the peaks. It has to keep well ahead of the foreground
			// mountain's hard bottom edge (350 units more travel, as in the original),
			// otherwise that edge shows above the fog on the right.
			.fromTo(q(".cloud1"), { y: 100 }, { y: -850 }, 0)
			// Hold the final frame.
			.to({}, { duration: END_HOLD });

		const arrowBtn = scene.querySelector(".arrow-btn");
		const onEnter = () =>
			gsap.to(q(".arrow"), { y: 10, duration: 0.8, ease: "back.inOut(3)", overwrite: "auto" });
		const onLeave = () =>
			gsap.to(q(".arrow"), { y: 0, duration: 0.5, ease: "power3.out", overwrite: "auto" });
		const onClick = () => {
			// Play the whole pinned sequence, landing exactly where the next section starts.
			const target = tl.scrollTrigger.end;
			if (ScrollToPlugin) {
				gsap.to(window, { scrollTo: target, duration: 2.5, ease: "power2.inOut" });
			} else {
				window.scrollTo({ top: target, behavior: "smooth" });
			}
		};

		arrowBtn.addEventListener("mouseenter", onEnter);
		arrowBtn.addEventListener("mouseleave", onLeave);
		arrowBtn.addEventListener("click", onClick);

		return () => {
			arrowBtn.removeEventListener("mouseenter", onEnter);
			arrowBtn.removeEventListener("mouseleave", onLeave);
			arrowBtn.removeEventListener("click", onClick);
			if (tl.scrollTrigger) tl.scrollTrigger.kill();
			tl.kill();
		};
	}, []);

	return (
		<section className="cloud-scene" ref={sceneRef}>
			<svg
				viewBox="0 0 1200 800"
				preserveAspectRatio="xMidYMid slice"
				xmlns="http://www.w3.org/2000/svg"
				role="img"
				aria-label="Snowy mountain peaks rising through the clouds"
			>
				<defs>
					{/* Fades the hero section's flat blue into the sky artwork so the two sections meet seamlessly. */}
					<linearGradient id="skyBlend" x1="0" y1="0" x2="0" y2="1">
						<stop offset="0" stopColor="#225d92" stopOpacity="1" />
						<stop offset="0.38" stopColor="#225d92" stopOpacity="1" />
						<stop offset="1" stopColor="#225d92" stopOpacity="0" />
					</linearGradient>
				</defs>

				<mask id="cloudMask">
					<g className="cloud1">
						<rect fill="#fff" width="100%" height="801" y="799" />
						<image href={`${ASSET_BASE}cloud1Mask.jpg`} width="1200" height="800" />
					</g>
				</mask>

				{/*
				  The sky artwork is only 590 tall on purpose: below it the white section
				  background shows through, and that white reads as fog under the foreground
				  peaks (exactly how the original scene hides their hard bottom edge).
				*/}
				<g className="sky">
					<image href={`${ASSET_BASE}sky.jpg`} width="1200" height="590" />
					<rect width="1200" height="590" fill="url(#skyBlend)" />
				</g>
				<image className="mountBg" href={`${ASSET_BASE}mountBg.png`} width="1200" height="800" />
				<image className="mountMg" href={`${ASSET_BASE}mountMg.png`} width="1200" height="800" />
				<image className="cloud2" href={`${ASSET_BASE}cloud2.png`} width="1200" height="800" />
				<image className="mountFg" href={`${ASSET_BASE}mountFg.png`} width="1200" height="800" />
				<image className="cloud1" href={`${ASSET_BASE}cloud1.png`} width="1200" height="800" />
				<image className="cloud3" href={`${ASSET_BASE}cloud3.png`} width="1200" height="800" />

				{/* Sits below the fixed navbar even on wide screens, where the top of the viewBox is cropped. */}
				<g className="headline">
					<text className="text-explore" fill="#fff" x="600" y="270" textAnchor="middle">
						EXPLORE
					</text>
					<polyline
						className="arrow"
						fill="#fff"
						points="599,310 599,349 590,339 590,342 600,352 610,342 610,339 601,349 601,310"
					/>
					<rect className="arrow-btn" width="100" height="100" opacity="0" x="550" y="280" />
				</g>

				<g className="further-layer" mask="url(#cloudMask)">
					<rect fill="#fff" width="100%" height="100%" />
					<text className="text-further" fill="#162a43" x="600" y="270" textAnchor="middle">
						FURTHER
					</text>
				</g>
			</svg>
		</section>
	);
};

export default Cloud;
